import React from "react";
import { useLocation } from "react-router-dom";
import { Button, Logo } from "../components/ui";

// #69: a render crash anywhere in the routed content (e.g. a screen indexing
// into state that a failed API call left undefined) previously unmounted the
// entire app — React has no default recovery from an uncaught error during
// render, so the user saw a fully blank page with no way back. This is the
// one boundary in the tree, placed around the route table so any single
// screen's crash degrades to an error message instead of a blank app.
class RouteErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("Unhandled error in routed content:", error, info);
  }

  componentDidUpdate(prevProps) {
    // A caught render error otherwise sticks forever: this boundary sits
    // above <Routes>, so client-side navigation (a Link, a back/forward, the
    // "Back to dashboard" button reduced to router.navigate) re-renders the
    // same tree without unmounting it, and neither getDerivedStateFromError
    // nor componentDidCatch fires again to clear it. Resetting whenever the
    // route key changes means the fallback only ever covers the screen that
    // actually crashed, not every screen after it for the rest of the tab.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="stars-dark tw-grid tw-min-h-screen tw-place-items-center tw-bg-bg tw-px-4 tw-font-sans tw-text-text">
          <div className="tw-grid tw-max-w-md tw-justify-items-center tw-gap-4 tw-text-center">
            <Logo className="tw-w-40" alt="" />
            <h2 className="tw-m-0 tw-text-xl tw-font-bold">Something went wrong</h2>
            <p className="tw-m-0 tw-text-sm tw-text-text-muted">
              This screen hit an unexpected error. Try going back, or
              reload the page.
            </p>
            <Button className="tw-mt-4" onClick={() => window.location.assign("/dashboard")}>
              Back to dashboard
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return (
    <RouteErrorBoundaryClass resetKey={location.key}>
      {children}
    </RouteErrorBoundaryClass>
  );
}
